import React, { useEffect, useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useScreenSize } from '@/utils/useScreenSize';
import { LicensePlateSearchForm } from './LicensePlateSearchForm/LicensePlateSearchForm';
import { VINSearchForm } from './VINSearchForm/VINSearchForm';
import { YMMSearchForm } from './YMMSearchForm/YMMSearchForm';
import { VehicleSearchProps } from './utils';
import { StyledTabsNavigationSearchHub } from '../../dashboard/SearchHub/components';
import { subscribe, unsubscribe } from '../../events';
// import { SearchData } from '../Header';
import { StyledHeaderTabsNavigation } from '../Header/components';

export interface VehicleSearchFormProps extends VehicleSearchProps {
  widerDropdowns?: boolean;
}

/**
 * This component displays the vehicle search options: VIN, License plate and Year, Make, Model.
 * It also displays the search form according to the selected option.
 */
export function VehicleSearchForm({
  onSuccess,
  onError,
  clearFields,
  vinInformationOptions = false,
  searchButtonText = 'Search',
  claimReport = false,
  isDashboardSearchHub = false, // tech debt
  searchButtonSize = '15%',
  mobileView = false,
  additionalTabs,
  placement,
  widerDropdowns = false,
  optionalProps,
  ddPlan = '',
}: VehicleSearchFormProps) {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);
  const { isMobile } = useScreenSize();
  const { trackEventGA4 } = useAppConfigContext();

  // this allows the active tab component to be controlled from the publish event
  useEffect(() => {
    subscribe('openVinSearch', () => {
      const index = internalTabs.findIndex((tab) => tab.label === 'VIN');

      setSelectedTabIndexOrSetToNull(index);
    });

    subscribe('openPlateSearch', () => {
      const index = internalTabs.findIndex(
        (tab) => tab.label === 'License Plate'
      );

      setSelectedTabIndexOrSetToNull(index);
    });

    subscribe('openYMMSearch', () => {
      const index = internalTabs.findIndex(
        (tab) => tab.label === 'Year, Make, Model'
      );

      setSelectedTabIndexOrSetToNull(index);
    });

    return () => {
      unsubscribe('openVinSearch', () => {
        setSelectedTabIndex(null);
      });

      unsubscribe('openPlateSearch', () => {
        setSelectedTabIndex(null);
      });

      unsubscribe('openYMMSearch', () => {
        setSelectedTabIndex(null);
      });
    };
  }, []);

  const setSelectedTabIndexOrSetToNull = (index: number) => {
    setSelectedTabIndex(index >= 0 ? index : null);
  };

  const handleTabClick = (eventCategory: string) => () => {
    //Event category could be VIN/YMM/Licence Plate.
    trackEventGA4({
      event_name: 'report_search',
      report_type: 'vehicle',
      search_by: eventCategory,
      search_from: `${placement}_search`,
    });
  };

  const internalTabs = [
    {
      label: 'VIN',
      content: (
        <VINSearchForm
          onSuccess={onSuccess}
          onError={onError}
          claimReport={claimReport}
          clearFields={clearFields}
          searchButtonText={searchButtonText}
          informationOptions={vinInformationOptions}
          searchButtonSize={searchButtonSize}
          mobileView={mobileView}
          placement={placement}
          optionalProps={optionalProps}
          ddPlan={ddPlan}
        />
      ),
      trackClick: handleTabClick('vin'),
    },
    {
      label: 'License Plate',
      content: (
        <LicensePlateSearchForm
          onSuccess={onSuccess}
          onError={onError}
          claimReport={claimReport}
          clearFields={clearFields}
          searchButtonText={searchButtonText}
          searchButtonSize={searchButtonSize}
          mobileView={mobileView}
          placement={placement}
          optionalProps={optionalProps}
          ddPlan={ddPlan}
        />
      ),
      trackClick: handleTabClick('license_plate'),
    },
  ];

  if (!claimReport) {
    internalTabs.push({
      label: 'Year, Make, Model',
      content: (
        <YMMSearchForm
          onSuccess={onSuccess}
          onError={onError}
          clearFields={clearFields}
          searchButtonText={searchButtonText}
          mobileView={mobileView}
          placement={placement}
          widerDropdowns={widerDropdowns}
          optionalProps={optionalProps}
        />
      ),
      trackClick: handleTabClick('ymm'),
    });
  }

  if (additionalTabs?.length) {
    internalTabs.push(
      ...additionalTabs.map((tab) => ({
        label: tab.label,
        content: tab.content,
        trackClick: tab.trackClick || (() => {}),
        customHandler: tab.customHandler || (() => {}),
      }))
    );
  }

  const TabsNavigation = isDashboardSearchHub
    ? StyledTabsNavigationSearchHub
    : StyledHeaderTabsNavigation; // Tech Debt: this is a temporary solution to avoid breaking the header search hub. We should abstract and replicate other search types in the header

  return (
    <TabsNavigation
      className={isMobile ? 'mobile' : 'desktop desktop--no-margin-left'}
      tabs={internalTabs}
      data-testid="vehicle-search-form"
      externalActiveTab={selectedTabIndex || undefined}
    />
  );
}
